// getUserBadges.js
'use strict';
import createCachedSelector, {
  createSelector,
} from '../resource/createCachedSelector.js';

import getMeData from '../selector/getMeData.js';
import getUserData from '../selector/getUserData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import i18n from '../resource/i18n.js';
import getLeaderboardBadgeData from '../resource/getLeaderboardBadgeData.js';
import getTranslationKey from '../resource/getTranslationKey.js';

import { LIVESTREAM_CARD_COUNTRY_FLAG_BLACKLIST } from '../RemoteConfigKeys.js';

export const COUNTRY_BADGE_PREFIX = 'country:';
export const CAMPAIGN_BADGE_PREFIX = 'campaign_';
export const ANIMATED_BADGE_SUFFIX = '_sprite';

const defaultArray = [];

/**
 * detect badge is campaign badge or not.
 * @param {string} {badge} - badge.
 * @return {boolean} return `true` when badge is campaign badge.
 */
export const isCampaignBadge = ({ badge }) =>
  badge.startsWith(CAMPAIGN_BADGE_PREFIX);

/**
 * detect badge is an animated (sprite) badge or not. Animated badges are
 * marked by a naming convention (suffix) so the client knows to fetch a
 * sprite sheet without probing every badge.
 * @param {object} payload - payload.
 * @param {string} payload.badge - badge.
 * @return {boolean} return `true` when badge is an animated badge.
 */
export const isAnimatedBadge = ({ badge }) =>
  badge.endsWith(ANIMATED_BADGE_SUFFIX);

/**
 * Checks whether a given badge belongs to the user-level badge category.
 *
 * The user level badge prefix is retrieved from remote config and follows
 * the format:
 *
 *   user_level_${YYYYMMDD}
 *
 * Since the date suffix may change over time, this selector removes the
 * trailing numeric date part using the regex /\d+$/ to obtain a stable
 * prefix for comparison.
 *
 * Example:
 *   userLevelBadgePrefix (remote config): "user_level_20260113"
 *   normalized prefix after regex:        "user_level_"
 *
 *   badge: "user_level_20260113_badge_simple_0"
 *   → startsWith("user_level_") === true
 *
 * @param {string} userLevelBadgePrefix - Badge prefix from remote config.
 * @param {string} badge - Badge identifier to be checked.
 * @return {boolean} Returns true if the badge is a user level badge.
 */
export const getIsUserLevelBadge = ({ userLevelBadgePrefix, badge }) =>
  badge.startsWith(userLevelBadgePrefix?.replace(/\d+$/, ''));

/**
 * detect badge is language badge or not.
 * @param {object} payload - payload.
 * @param {string} payload.badge - badge.
 * @return {boolean} return `true` when badge is language badge.
 */
export const isLanguageBadge = ({ badge }) => badge.startsWith('lang:');

/**
 * detect badge is swagger badge or not.
 * @param {string} {badge} - badge.
 * @return {boolean} return `true` when badge is swagger badge.
 */
export const isSwaggerBadge = ({ badge }) => /^swagger$/.test(badge);

/**
 * detect badge is location badge or not.
 * @param {object} payload - payload.
 * @param {string} payload.badge - badge.
 * @return {boolean} return `true` when badge is language badge.
 */
export const isLocationBadge = ({ badge }) => badge.startsWith('location:');

/**
 * detect badge is country badge or not.
 * @param {object} payload - payload.
 * @param {string} payload.badge - badge.
 * @return {boolean} return `true` when badge is country badge.
 */
export const isCountryBadge = ({ badge }) =>
  badge.startsWith(COUNTRY_BADGE_PREFIX);

/**
 * detect badge has translation or not.
 * @param {object} payload - payload.
 * @param {string} payload.badge - badge.
 * @return {boolean} return `true` when badge has translation.
 */
export const hasTranslation = ({ badge }) => {
  let i18nKey = badge;

  const { isLeaderboardBadge, region } = getLeaderboardBadgeData({
    badgeName: badge,
  });

  if (isLeaderboardBadge) {
    i18nKey = `leaderboard_${region}`;
  }

  return (
    i18n.customExists(i18nKey) ||
    i18n.customExists(getTranslationKey({ key: `badge_${badge}` }))
  );
};

/**
 * Select user badges by user id
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} userId - user id.
 * @param {String} type - the type string. ex: [null, 'langs', 'locations']
 * @return {Array} The selected user badges.
 */
const getUserBadges = createCachedSelector(
  (state, userId) => getBadges(state, userId),
  (state, userId, type) => type,
  state => getRemoteConfigData(state, LIVESTREAM_CARD_COUNTRY_FLAG_BLACKLIST),
  (badges = defaultArray, type, countryFlagBlacklist = []) => {
    if ('hasSwaggerBadge' === type) {
      return badges.includes('swagger');
    }
    if ('minimumBadges' === type) {
      let leaderboardBadge;
      let leaderboardBadgePlace;
      const restBadges = [];
      [...badges].reverse().forEach(badge => {
        const { isLeaderboardBadge, place } = getLeaderboardBadgeData({
          badgeName: badge,
        });
        if (isLeaderboardBadge) {
          if (!leaderboardBadge || leaderboardBadgePlace > place) {
            leaderboardBadge = badge;
            leaderboardBadgePlace = place;
          }
          return;
        }
        if (
          isLocationBadge({ badge }) ||
          isCountryBadge({ badge }) ||
          isLanguageBadge({ badge }) ||
          !hasTranslation({ badge })
        ) {
          return;
        }
        if (restBadges.length < 2) {
          restBadges.unshift(badge);
          return;
        }
      });
      return [leaderboardBadge, ...restBadges].filter(badge => badge);
    }
    const result = [
      ...new Set(
        badges.filter(badge => {
          if (type === 'locations') {
            return isLocationBadge({ badge });
          }
          if (type === 'countries') {
            return isCountryBadge({ badge });
          }
          if (type === 'countryFlags') {
            return (
              isCountryBadge({ badge }) &&
              !countryFlagBlacklist.some(country => badge.includes(country))
            );
          }
          if (type === 'langs') {
            return isLanguageBadge({ badge });
          }
          if (
            !isCountryBadge({ badge }) &&
            !isLanguageBadge({ badge }) &&
            hasTranslation({ badge }) &&
            !isSwaggerBadge({ badge })
          ) {
            return true;
          }
          return false;
        })
      ),
    ];
    return result;
  }
)((state, userId, type) => `${userId}:${type}`);

export default getUserBadges;

/**
 * Select badges
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} userId - user id.
 * @return {Array} The selected user badges.
 */
const getBadges = createCachedSelector(
  (state, userId) => userId,
  (state, userId) => getUserData(state, userId, 'badges'),
  state => getMeData(state, 'id'),
  state => getMeData(state, 'badges'),
  (userId, userBadges = defaultArray, meId, meBadges = defaultArray) => {
    if (userId === meId) {
      return meBadges;
    }
    return userBadges;
  }
)((state, userId) => `${userId}`);

/**
 * Select my badges for an optimistic livestream chat message, mirroring the
 * server-side `sender_badges` whitelist (campaign badges only, order kept).
 * Deduped because me.badges can hold duplicates, which break React keys.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {Array} The badges to show before the server echo arrives.
 * @example
 * getMeSenderBadges(state);
 * // me.badges: ['country:tw', 'campaign_party', 'hot', 'campaign_heart_sprite']
 * // => ['campaign_party', 'campaign_heart_sprite']
 */
export const getMeSenderBadges = createSelector(
  state => getMeData(state, 'badges'),
  (badges = defaultArray) => [
    ...new Set(badges.filter(badge => isCampaignBadge({ badge }))),
  ]
);
